<template>
    <div class="a-form-inputs">
        <div class="u-d-flex u-flex-center u-r-flex-wrap">
            <h2 class="u-centre chart u-margin-bottom-20 u-r-flex-1-0-100">
                Analyse Claims by Claims Handler
            </h2>
            <span class="fee-earner" @click="openModal">
                <icon-tooltip icon-type="info" tooltip-text="Click to see details of the different options"
                              side="left" text-width="200"></icon-tooltip>
            </span>
        </div>
        <div class="p-compare-reserve__fee-earner-chart-select">
            <select v-model="compareType">
                <option value="Total Difference">Total Difference</option>
                <option value="Average Difference">Average Difference</option>
                <option value="Maximum Difference">Maximum Difference</option>
                <option value="Number of Claims">Number of Claims</option>
            </select>
        </div>
        <bar-chart v-if="show && theCurrentWidth >= 600" :chart-data="currentChartData" :options="chartOptions" :height="200"></bar-chart>

        <table class="a-table" v-if="theCurrentWidth < 600">
            <th>Fee Earner</th>
            <th>{{compareType}}</th>
            <tr v-for="(info, index) in currentChartData.labels">
                <td>{{currentChartData.labels[index]}}</td>
                <td v-if="compareType !== 'Number of Claims'">{{currencyFormat(currentChartData.datasets[0].data[index])}}</td>
                <td v-else>{{currentChartData.datasets[0].data[index]}}</td>
            </tr>
        </table>
        
        <modal name="feinfo" no-default-button="true">
            <h2>Different options for comparing Claims Handlers.</h2>
            <p><strong>Total Difference</strong></p>
            <p>The total difference from '{{fromLabel}}' to '{{toLabel}}' grouped by Claims Handler for all the claims
                matching your filter criteria.</p>
            <p><strong>Average Difference</strong></p>
            <p>The average difference from '{{fromLabel}}' to '{{toLabel}}' for each Claims Handler for all the claims
                matching your filter criteria.</p>
            <p><strong>Maximum Difference</strong></p>
            <p>For each Claims Handler the case that has the maximum difference from '{{fromLabel}}' to '{{toLabel}}'
                matching your filter criteria.</p>
            <p><strong>Total Claims</strong></p>
            <p>For each Claims Handler the total number of claims that match your filter criteria for each Claims Handler.</p>
            <br>
            <p>NOTE: Any Claims Handler that does not have a result is omitted.</p>
        </modal>
    </div>
</template>

<script>
    import _ from 'lodash';
    import {mapGetters} from 'vuex';
    import BarChart from 'components/graphs/BarChart';
    import Control from "components/form/Control.vue";
    import Paginator from "components/Paginator";
    import currencyFormat from 'mixins/currencyFormat';
    import IconTooltip from 'components/IconTooltip';
    import Modal from 'components/modal/Modal';
    import ModalInTheWild from 'components/modal/ModalInTheWild';
    import roundValue from "mixins/roundValue";

    const labels = {
        "dayOne": 'Day One Reserve ',
        "paid": 'Paid',
        "final": 'Final Reserve',
        "initial": 'Initial Reserve',
    };

    export default {
        name: 'fee-earner-chart',
        components: {
            Paginator,
            Control,
            BarChart,
            IconTooltip,
            Modal,
            ModalInTheWild,
            roundValue,
            currencyFormat
        },
        mixins: [
            currencyFormat
        ],
        data() {
            return {
                compareType: 'Total Difference',
                pageNo: 0,
                show: true
            }
        },
        watch: {
            compareType() {
                this.show = false;
                setTimeout(() => this.show = true, 1);
            }
        },
        computed: {
            ...mapGetters('reserves/compare', ['claims', 'from', 'to']),
            ...mapGetters('windowWidth', ['theCurrentWidth']),
            feeEarnerAggregate() {
                let total = this.claims.length;
                return _.chain(this.claims)
                    .reduce((list, claim) => {
                        if (!list[claim.claimHandler] && list[claim.claimHandler] !== 0) {
                            list[claim.claimHandler] = 0;
                        }
                        switch (this.compareType) {
                            case "Total Difference":
                            case "Average Difference":
                                list[claim.claimHandler] += Math.abs(claim.difference);
                                break;
                            case "Maximum Difference":
                                if (list[claim.claimHandler] < claim.difference) {
                                    list[claim.claimHandler] = claim.difference;
                                }
                                break;
                            case "Number of Claims":
                                list[claim.claimHandler]++;
                        }
                        return list;
                    }, {})
                    .toPairs()
                    .sortBy(value => -Math.abs(value[1]))
                    .map(value => { //It's hack but i'm tired
                        if (this.compareType === 'Average Difference') {
                            value[1] = value[1] / total;
                        }
                        return value;
                    })
                    .fromPairs()
                    .value();
            },
            feeEarnersSliced() {
                return _.chain(this.feeEarnerAggregate)
                    .toPairs()
                    .slice(this.pageNo * 5, (this.pageNo + 1) * 5)
                    .fromPairs()
                    .value();
            },

            fromLabel() {
                return labels[this.from];
            },
            toLabel() {
                return labels[this.to];
            },
            /**
             * Chart JS options for a bat chart.
             *
             * Bar: http://www.chartjs.org/docs/latest/charts/bar.html
             *
             * @return {{}}
             */
            currentChartData() {
                return {
                    type: 'bar',
                    labels: Object.keys(this.feeEarnersSliced),
                    datasets: [
                        //Line Chart
                        {
                            backgroundColor: [
                                '#45BFFC',
                                '#3DF2C3',
                                '#CD5CD8',
                                '#EB1458',
                                '#7DD3F4',
                                '#94F7DB'
                            ],
                            data: _.values(this.feeEarnersSliced)
                        },
                    ]
                }
            },
            chartOptions() {
                return {
                    legend: {display: false},
                    scales: {
                        xAxes: [{
                            categoryPercentage: 0.8,
                            barPercentage: 0.8,
                            ticks: {
                                fontSize: 14,
                                fontFamily: "'Nunito', sans-serif",
                                fontColor: "#000",
                            },
                        }],
                        yAxes: [{
                            type: 'linear',
                            ticks: {
                                callback: (label) => {
                                    if (this.compareType !== 'Number of Claims') {
                                        return this.currencyFormat(label, '£', 0);
                                    }

                                    if (Math.floor(label) === label) {
                                        return label;
                                    }
                                },
                                fontSize: 16,
                                fontFamily: "'Nunito', sans-serif",
                                fontColor: "#000",
                                stepSize: this.compareType !== 'Number of Claims' ? null : this.claims.length / 10
                            },
                            gridLines: {
                                display: true,
                                drawTicks: false,
                                color: 'rgba(255, 255, 255, 0)',
                                zeroLineWidth: 1,
                                zeroLineColor: 'rgba(0, 0, 0, 0.20)',
                                drawBorder: false
                            }
                        }]
                    },
                    tooltips: {
                        mode: 'single',
                        callbacks: {
                            label: tooltipItems => this.compareType !== 'Number of Claims' ? this.currencyFormat(tooltipItems.yLabel) : tooltipItems.yLabel
                        }
                    },
                }
            },
            pageNumbers() {
                return Math.ceil(Object.keys(this.feeEarnerAggregate).length / 5);
            }
        },
        methods: {
            changePage(page) {
                this.pageNo = page;
            },
            openModal() {
                this.$store.dispatch('modal/toggle', 'feinfo');
            },
        }
    }
</script>


// WEBPACK FOOTER //
// FeeEarnerChart.vue?117482f5